import {FC, useEffect, useRef, useState} from 'react';
import {Link, useNavigate} from 'react-router-dom';
//mui
import {Box, Typography} from '@mui/material';
//icons
import {ReactComponent as LogoIcon} from '@/assets/icons/logo-title.svg';
import {ReactComponent as ArrowLeftIcon} from '@/assets/icons/arrow-line-left.svg';
import {ReactComponent as ArrowUpIcon} from '@/assets/icons/arrow-line-up.svg';
//constants
import {BACK, BODY2, H4, SMOOTH, SUBTITLE1, TEXT} from '@/utils/constants';
//components
import Button from '@/shared/components/Button';
//styles
import styles from './styles.module.scss';

const SCROLL_TOP_THRESHOLD = 400;

const PrivacyPolicy: FC = () => {
  const navigate = useNavigate();

  const listRef = useRef<HTMLDivElement>(null);

  const [isScrolledDown, setIsScrolledDown] = useState(false);

  const handleBackButtonClick = () => navigate(-1);
  const handleUpButtonClick = () => listRef.current?.scrollIntoView({behavior: SMOOTH});
  const handleAnchorClick = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({behavior: SMOOTH});
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolledDown(window.scrollY > SCROLL_TOP_THRESHOLD);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box className={styles.wrapper} ref={listRef}>
      <Box className={styles.title}>
        <LogoIcon className={styles.title__logo} />

        <Typography variant={H4} className={styles.title__header}>
          Privacy Policy
        </Typography>

        <Typography variant={BODY2} className={styles.title__description}>
          Effective Date: April 27, 2026
        </Typography>

        <Button
          title={BACK}
          className={styles.button__back}
          startIcon={<ArrowLeftIcon />}
          onClick={handleBackButtonClick}
        />
      </Box>

      <Box className={styles.content}>
        <Box className={styles.content__info}>
          <Typography variant={SUBTITLE1} className={styles.content__info__title}>
            The short version
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Event Simpli is software that nonprofits and other organizations use to run fundraising events — ticketing,
            silent auctions, donations, and registration. This Privacy Policy explains what we collect, why, and what
            you can do about it. We&apos;ve kept the language plain. If you only read one section, read this one.
            <br />
            <br />
            • If you sign up an organization, we collect the information needed to run your account and your events.
            <br />
            • If you donate, buy a ticket, bid, or register through an event page on our platform, the organization
            running the event is the primary owner of your information; we host it for them.
            <br />
            • We do not sell your information for money. We share it with the vendors that help us run the Service (such
            as our payment processors, cloud host, and email/SMS providers).
            <br />
            • We protect your information with TLS encryption, role-based access, multi-factor authentication, and
            standard industry safeguards.
            <br />• You have the right to see, correct, delete, and export your information. How you exercise these
            rights depends on where you live; the details are below.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title}>
            Privacy Summary
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-1">
            1. Who this policy covers
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Event Simpli LLC (“Event Simpli,” “we,” “us,” or “our””), a Texas limited liability company, operates the
            website at eventsimpli.com, the Event Simpli application, and the event pages we host for our customers. We
            refer to all of that together as the “Service.” This Privacy Policy applies to everyone who uses any part of
            the Service, including visitors to our marketing site, customer organizations and their staff, and donors
            and attendees who interact with an event page.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-2">
            2.Our role
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Privacy roles depend on whose information we&apos;re talking about.
            <br />
            <br />
            • For information we collect about our own customers, prospects, and visitors to eventsimpli.com, we are the
            controller (or, under U.S. state laws, the “business”). This includes things like your account email,
            billing details, and how you use our marketing site.
            <br />• For donor, attendee, and registrant information that customer organizations submit to or collect
            through our application, the organization is the controller (or “business”) and we are the processor (or
            “service provider”). We process that information on the organization&apos;s instructions, as set out in our
            Data Processing Addendum (available on request at dpa@eventsimpli.com). If you are a donor or attendee with
            questions about how an organization uses your information, please contact that organization first. We will
            help route your request, but the organization is responsible for fulfilling it.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-3">
            3. What we collect
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Information you give us
            <br />
            <br />
            • Account and contact information: name, work email, phone number, organization name, role, password (stored
            as a salted hash).
            <br />
            • Billing information: billing contact, billing address, plan selection. Payment cards are handled by our
            payment processors — we receive a token, the last four digits, and the expiration date.
            <br />
            • Event content: event names, descriptions, branding, schedules, ticket types, auction items, donation
            pages, and messaging.
            <br />
            • Donor and attendee data (from customers, or from end users on event pages): name, email, phone, mailing
            address, table number, meal preferences, communication preferences, donor notes, and custom fields the
            organization sets up.
            <br />
            • Transaction data: ticket purchases, auction bids and wins, donation amounts, processing-fee coverage,
            payment provider, status, and refunds.
            <br />
            • Communications: emails and texts sent through the Service, opt-in and opt-out records, and your messages
            to our support team.
            <br />
            • Security data: two-factor authentication settings and password-reset tokens. Information we collect
            automatically
            <br />
            • Device and log data: IP address, browser, operating system, language, referring pages, time stamps, and
            clickstream data.
            <br />
            • Cookies and similar technologies (see Section 4).
            <br />
            • Usage analytics: pages viewed, features used, errors encountered, and performance metrics.
            <br />
            • Approximate location derived from IP address, used for security and aggregate analytics.
            <br />
            <br />
            Information we get from others
            <br />
            <br />
            • Single sign-on providers (such as Google), if you log in that way: name, email, profile image, and an
            account ID.
            <br />
            • Payment processors (Stripe, PayPal, Square): tokenized card details, payment status, dispute
            notifications, and fraud-risk signals.
            <br />• Marketing and analytics partners: aggregated audience data about how people find and engage with our
            marketing materials.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-4">
            4. Cookies and similar technologies
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Cookies are small files placed on your device when you visit a website. We use them — together with pixels,
            local storage, and similar tools — to make the Service work, remember your preferences, measure performance,
            and (on our marketing site only) support marketing. We use four categories:
          </Typography>

          <table className={styles.content__info__table}>
            <thead className={styles.content__info__table__head}>
              <tr>
                <th>Category</th>
                <th>What it does</th>
                <th>Your control</th>
              </tr>
            </thead>
            <tbody className={styles.content__info__table__body}>
              <tr>
                <td>
                  <strong>Strictly necessary</strong>
                </td>
                <td>
                  Authentication, session management, security, fraud prevention. The Service won&apos;t work without
                  these.
                </td>
                <td>Cannot be turned off.</td>
              </tr>
              <tr>
                <td>
                  <strong>Functional</strong>
                </td>
                <td>Remembers preferences such as language, last-visited tab, or UI state.</td>
                <td>Can be disabled in cookie preferences.</td>
              </tr>
              <tr>
                <td>
                  <strong>Analytics</strong>
                </td>
                <td>Helps us understand how the Service is used so we can improve it.</td>
                <td>Consent required in regions that require it (EEA, UK, Switzerland).</td>
              </tr>
              <tr>
                <td>
                  <strong>Marketing</strong>
                </td>
                <td>
                  Used only on eventsimpli.com to measure marketing and, where enabled, deliver relevant ads on
                  third-party platforms.
                </td>
                <td>Consent required where applicable; honor Global Privacy Control.</td>
              </tr>
            </tbody>
          </table>

          <Typography variant={BODY2} className={styles.content__info__description}>
            You can manage non-essential cookies on our marketing site via the cookie banner or the Cookie Preferences
            link in the footer. You can also use your browser controls or industry opt-outs
            (optout.networkadvertising.org, youradchoices.com, youronlinechoices.eu). On customer event pages we use
            only strictly necessary cookies plus anything the organization explicitly enables.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-5">
            5. Why we use information
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            <br />
            • To run the Service: authentication, hosting your event pages, processing transactions, sending
            transactional notifications, and helping you with support.
            <br />
            • To bill for subscriptions and manage renewals.
            <br />
            • To keep the Service secure, prevent fraud, debug errors, and enforce our Terms.
            <br />
            • To improve the Service — measuring usage, A/B testing, and building new features. Where we use customer
            data for improvement, we work with de-identified or aggregated data.
            <br />
            • To communicate with you about updates, security issues, and (subject to your preferences) marketing.
            <br />• To comply with law and respond to lawful requests.
            <br />
            <br />
            Lawful bases (EEA, UK, Switzerland) Where GDPR or UK GDPR applies, we rely on these lawful bases:
            performance of a contract (to provide the Service); legitimate interests (to secure the Service, prevent
            fraud, and grow our business); consent (for certain marketing and non-essential cookies — you can withdraw
            it at any time); and legal obligation (taxes, accounting, regulatory reporting).
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-6">
            6. Who we share information with
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            • The customer organization — when an end user registers, donates, or bids on an event page.
            <br />
            • That organization&apos;s authorized users — its staff, board members, volunteers, or contractors who have
            been granted access.
            <br />
            • Our service providers and sub-processors — vendors that help us run the Service (hosting, database, email,
            SMS, analytics, fraud prevention, customer support, and security). Examples include AWS, Vercel,
            Twilio/SendGrid, Cloudflare, Sentry/Datadog, and our customer-support tools. A current list is in our Data
            Processing Addendum.
            <br />
            • Payment processors — Stripe, PayPal, and Square — to process payments on behalf of our customer
            organizations. Their handling of card data is governed by their own terms.
            <br />
            • Professional advisors — lawyers, auditors, and accountants under appropriate confidentiality obligations.
            <br />
            • In a corporate transaction — for example, a merger, acquisition, financing, or sale of assets, with
            standard confidentiality protections.
            <br />
            • To comply with law — when required by valid legal process or to protect rights, property, or safety.
            <br />
            • With your consent or at your direction.
            <br />
            <br />
            We do not sell personal information for money. Some advertising on our marketing site may constitute
            “sharing” for cross-context behavioral advertising under certain U.S. state laws if enabled. You can opt out
            as described in Section 9.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-7">
            7. Text messaging
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            If you opt in to receive SMS through the Service, we deliver those messages through licensed carriers (such
            as Twilio) on behalf of the organization sending them. We do not share or sell SMS opt-in data to third
            parties for marketing. Message and data rates may apply. You can opt out at any time by replying STOP, or
            get help by replying HELP. Our Mobile Terms (also linked at eventsimpli.com) explain this in more detail.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-8">
            8. Children
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            The Service is not directed to children under 13, and we do not knowingly collect information from them. If
            you believe we have, contact privacy@eventsimpli.com and we will delete it. Organizations are responsible
            for making sure their event registrations and donation pages comply with COPPA and similar laws if
            they&apos;re likely to attract minors.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-9">
            9. Your rights and choices
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            <br />
            U.S. residents
            <br />
            <br />
            Depending on your state — California, Colorado, Connecticut, Virginia, Utah, and others — you may have
            rights to access, correct, delete, port, and limit the use of your personal information; to opt out of
            “sale,” “sharing,” targeted advertising, and certain profiling; and to limit the use of sensitive personal
            information. To exercise these rights, email privacy@eventsimpli.com or use the privacy request form on our
            site. We will verify your request using information already associated with your account; if you are an end
            user, we&apos;ll work with the relevant organization. You may use an authorized agent. California: We have
            not sold personal information for money in the prior 12 months. We honor the Global Privacy Control signal
            where required by law as an opt-out of “sale” and “sharing.”
            <br />
            <br />
            EEA, UK, and Switzerland
            <br />
            <br />
            Under GDPR and UK GDPR you have rights to access, rectification, erasure, restriction of processing,
            portability, objection (including objection to direct marketing), and to withdraw consent. You can also
            lodge a complaint with a supervisory authority. Contact privacy@eventsimpli.com to exercise these rights.
            <br />
            <br />
            Canada
            <br />
            <br />
            Under PIPEDA you can request access to and correction of your personal information. You may withdraw consent
            subject to legal and contractual restrictions. Contact privacy@eventsimpli.com, or the Office of the Privacy
            Commissioner of Canada if you have a concern we can&apos;t resolve.
            <br />
            <br />
            Marketing choices
            <br />
            <br />
            • Email: click the unsubscribe link in any marketing email, or email privacy@eventsimpli.com.
            <br />
            • SMS: reply STOP to a marketing message.
            <br />
            • Cookies: use the banner on our marketing site or your browser settings.
            <br />
            <br />
            Even after unsubscribing from marketing, you will continue to receive transactional messages necessary to
            operate your account or the event you registered for.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-10">
            10. How long we keep information
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We keep information for as long as we need it to provide the Service, meet legal obligations, resolve
            disputes, and enforce our agreements.
            <br />
            <br />
            • Account information: for the life of the account, plus a reasonable period after closure (typically up to
            seven years for tax and accounting purposes).
            <br />
            • Customer Data: for the term of the subscription and a brief export window after termination, then deleted
            in the ordinary course.
            <br />
            • Transaction records: as required by financial-recordkeeping laws (typically seven years).
            <br />
            • Logs and security telemetry: typically up to 13 months.
            <br />• Marketing data: until you unsubscribe or we no longer have a legitimate use.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-11">
            11. Security
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We use industry-standard safeguards: TLS in transit, encryption of sensitive data at rest, role-based access
            with least privilege, multi-factor authentication for our personnel, vulnerability scanning, and regular
            backups. No system is 100% secure, so we can&apos;t promise absolute security. If a breach affects your
            information, we&apos;ll notify you in line with applicable law.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-12">
            12. International transfers
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We&apos;re based in the United States. When personal information moves from the EEA, UK, or Switzerland to a
            country without an adequacy decision, we rely on appropriate safeguards — typically the European
            Commission&apos;s Standard Contractual Clauses, the UK International Data Transfer Addendum, and the Swiss
            FDPIC&apos;s modifications. For a copy of the clauses, email dpa@eventsimpli.com.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-13">
            13. Third-party links and services
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Our Service may link to or integrate with third-party services. We don&apos;t control them, and their
            privacy practices are governed by their own policies.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-14">
            14. Changes to this policy
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            We may update this policy from time to time. If we make material changes, we&apos;ll notify account holders
            by email or through the Service before the changes take effect. The Effective Date at the top shows when it
            was last updated.
          </Typography>

          <Typography variant={SUBTITLE1} className={styles.content__info__title} id="section-15">
            15. Contact us
          </Typography>

          <Typography variant={BODY2} className={styles.content__info__description}>
            Privacy: privacy@eventsimpli.com
            <br />
            Data Processing / DPA: dpa@eventsimpli.com
            <br />
            Event Simpli LLC
            <br />
            EU/UK representative: We will appoint an EU and UK GDPR representative under Article 27 if and when we are
            required to do so. Once appointed, the representative&apos;s contact details will be listed here.
          </Typography>
        </Box>

        <Box className={styles.content__menu}>
          <Typography variant={SUBTITLE1} className={styles.content__menu__title}>
            Table of contents
          </Typography>

          <ul className={styles.content__menu__list}>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-1" onClick={handleAnchorClick('section-1')}>
                Who this policy covers.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-2" onClick={handleAnchorClick('section-2')}>
                Our role.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-3" onClick={handleAnchorClick('section-3')}>
                What we collect.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-4" onClick={handleAnchorClick('section-4')}>
                {' '}
                Cookies and similar technologies.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-5" onClick={handleAnchorClick('section-5')}>
                Why we use information.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-6" onClick={handleAnchorClick('section-6')}>
                Who we share information with.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-7" onClick={handleAnchorClick('section-7')}>
                Text messaging.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-8" onClick={handleAnchorClick('section-8')}>
                Children.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-9" onClick={handleAnchorClick('section-9')}>
                Your rights and choices.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-10" onClick={handleAnchorClick('section-10')}>
                How long we keep information.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-11" onClick={handleAnchorClick('section-11')}>
                Security.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-12" onClick={handleAnchorClick('section-12')}>
                International transfers.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-13" onClick={handleAnchorClick('section-13')}>
                Third-party links and services.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-14" onClick={handleAnchorClick('section-14')}>
                Changes to this policy.
              </Link>
            </li>
            <li className={styles.content__menu__list__item}>
              <Link to="#section-15" onClick={handleAnchorClick('section-15')}>
                Contact us.
              </Link>
            </li>
          </ul>

          {isScrolledDown && (
            <Button
              title="Back to top"
              variant={TEXT}
              disableRipple
              className={styles.button__top}
              endIcon={<ArrowUpIcon />}
              onClick={handleUpButtonClick}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
